import React from 'react';
import { motion } from 'framer-motion';
import {
    MessageCircle, Users, Calendar, Trophy, Youtube, Twitch, Twitter,
    Swords, Heart, Shield, Star, Crown,
    Award, Gift, Home, Bell, Hash
} from 'lucide-react';
import CustomCursor from './CustomCursor';

const Community = () => {
    const socialPlatforms = [
        {
            name: "Discord",
            icon: <MessageCircle className="w-12 h-12" />,
            members: "2,500+",
            description: "Notre hub principal. Chat en temps réel, support, annonces et événements",
            color: "from-indigo-500 to-purple-600",
            link: "https://discord.gg/3wS59h3Vbu"
        },
        {
            name: "YouTube",
            icon: <Youtube className="w-12 h-12" />,
            members: "1,200+",
            description: "Tutoriels, guides et highlights des meilleurs moments du serveur",
            color: "from-red-500 to-red-600",
            link: "#"
        },
        {
            name: "Twitch",
            icon: <Twitch className="w-12 h-12" />,
            members: "800+",
            description: "Streams en direct, événements spéciaux et interactions avec la communauté",
            color: "from-purple-500 to-purple-700",
            link: "#"
        },
        {
            name: "Twitter",
            icon: <Twitter className="w-12 h-12" />,
            members: "1,500+",
            description: "Actualités rapides, updates et annonces importantes",
            color: "from-blue-400 to-blue-600",
            link: "#"
        }
    ];

    const communityFeatures = [
        {
            icon: <Users />,
            title: "Communauté Active",
            description: "Plus de 2,500 joueurs actifs avec une moyenne de 100-150 connectés en simultané",
            stat: "2,500+"
        },
        {
            icon: <Calendar />,
            title: "Événements Hebdomadaires",
            description: "Tournois, chasses shinies, raids et événements spéciaux chaque semaine",
            stat: "4-5/semaine"
        },
        {
            icon: <Trophy />,
            title: "Système de Récompenses",
            description: "Gagne des récompenses exclusives en participant aux événements communautaires",
            stat: "100+ prix"
        },
        {
            icon: <Shield />,
            title: "Modération Active",
            description: "Staff disponible 24/7 pour assurer une expérience de jeu agréable et sécurisée",
            stat: "15 Staff"
        }
    ];

    const upcomingEvents = [
        {
            title: "Tournoi PvP - Singles",
            date: "Samedi 20h",
            reward: "30,000 PokéCoins + Titre exclusif",
            icon: <Swords className="w-8 h-8" />,
            color: "from-red-500 to-pink-600"
        },
        {
            title: "Chasse Shiny Communautaire",
            date: "Dimanche 14h",
            reward: "Taux Shiny x3 + Boosts",
            icon: <Star className="w-8 h-8" />,
            color: "from-yellow-400 to-orange-500"
        },
        {
            title: "Raid Légendaire",
            date: "Mercredi 19h",
            reward: "Capture garantie + Objets rares",
            icon: <Crown className="w-8 h-8" />,
            color: "from-purple-500 to-indigo-600"
        },
        {
            title: "Construction Contest",
            date: "Vendredi 21h",
            reward: "50,000 PokéCoins + Showcase",
            icon: <Award className="w-8 h-8" />,
            color: "from-green-500 to-emerald-600"
        }
    ];

    const staffTeam = [
        {
            role: "Fondateur",
            name: "MasterTrainer",
            badge: <Crown className="w-6 h-6" />,
            color: "from-yellow-400 to-orange-500",
            description: "Créateur et développeur principal"
        },
        {
            role: "Admin",
            name: "PokéMaster",
            badge: <Shield className="w-6 h-6" />,
            color: "from-red-500 to-pink-600",
            description: "Gestion du serveur et des événements"
        },
        {
            role: "Modérateur",
            name: "GuardianPikachu",
            badge: <Star className="w-6 h-6" />,
            color: "from-blue-500 to-indigo-600",
            description: "Modération et support joueurs"
        },
        {
            role: "Helper",
            name: "FriendlyEevee",
            badge: <Heart className="w-6 h-6" />,
            color: "from-pink-400 to-rose-500",
            description: "Aide et accompagnement des nouveaux"
        }
    ];

    const discordChannels = [
        { icon: <Hash />, name: "annonces", description: "Toutes les news importantes" },
        { icon: <Hash />, name: "général", description: "Discussion libre" },
        { icon: <Hash />, name: "aide", description: "Support et questions" },
        { icon: <Hash />, name: "échanges", description: "Marketplace Pokémon" },
        { icon: <Hash />, name: "événements", description: "Calendrier et inscriptions" },
        { icon: <Hash />, name: "screenshots", description: "Partage tes captures" }
    ];

    return (
        <div className="relative min-h-screen bg-white overflow-hidden">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Nunito', sans-serif;
                    overflow-x: hidden;
                }

                .shine-effect {
                    position: relative;
                    overflow: hidden;
                }

                .shine-effect::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                    transition: left 0.5s;
                }

                .shine-effect:hover::before {
                    left: 100%;
                }
            `}</style>

            <CustomCursor />

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-4"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="relative w-14 h-14">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full border-2 border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </motion.a>

                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>
                </div>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-purple-600 via-indigo-700 to-blue-800 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-400 rounded-full blur-3xl"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="text-8xl mb-6"
                        >
                            🎉
                        </motion.div>
                        <h1 className="text-6xl lg:text-8xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Communauté
                        </h1>
                        <p className="text-2xl text-purple-100 max-w-3xl mx-auto font-semibold">
                            Rejoins une communauté passionnée de plus de 2,500 dresseurs Pokémon
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Social Platforms */}
            <section className="py-24 px-6 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Rejoins-nous Sur
                        </h2>
                        <p className="text-xl text-gray-600 font-semibold">
                            Suis-nous sur toutes les plateformes
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {socialPlatforms.map((platform, i) => (
                            <motion.a
                                key={i}
                                href={platform.link}
                                target="_blank"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -10, scale: 1.02 }}
                                className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border-4 border-gray-200 hover:border-transparent"
                            >
                                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>

                                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${platform.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                                    {platform.icon}
                                </div>

                                <h3 className="text-3xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {platform.name}
                                </h3>
                                <div className="text-2xl font-black text-red-600 mb-4">
                                    {platform.members}
                                </div>
                                <p className="text-gray-600 font-semibold">
                                    {platform.description}
                                </p>
                            </motion.a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Discord Section */}
            <section className="py-24 px-6 bg-gradient-to-br from-indigo-600 via-purple-700 to-indigo-800">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <MessageCircle className="w-20 h-20 text-white mx-auto mb-6" />
                        <h2 className="text-5xl font-black text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Notre Discord
                        </h2>
                        <p className="text-xl text-indigo-100 font-semibold mb-8">
                            Le cœur de notre communauté
                        </p>
                        <motion.button
                            className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-white text-indigo-600 font-black text-xl shine-effect hover:bg-yellow-400 hover:text-indigo-700 transition-all shadow-2xl"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -3 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <MessageCircle className="w-6 h-6" />
                            REJOINDRE LE DISCORD
                        </motion.button>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                        {discordChannels.map((channel, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-white/20"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="text-white">{channel.icon}</div>
                                    <h4 className="text-xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {channel.name}
                                    </h4>
                                </div>
                                <p className="text-indigo-100 font-semibold">
                                    {channel.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Community Features */}
            <section className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Une Communauté Vivante
                        </h2>
                        <p className="text-xl text-gray-600 font-semibold">
                            Ce qui rend PokeMovie unique
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {communityFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="text-center"
                            >
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center text-white mb-4 mx-auto">
                                    {feature.icon}
                                </div>
                                <div className="text-4xl font-black text-red-600 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {feature.stat}
                                </div>
                                <h3 className="text-xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 font-semibold">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Upcoming Events */}
            <section className="py-24 px-6 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <Calendar className="w-16 h-16 text-red-600 mx-auto mb-4" />
                        <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Événements à Venir
                        </h2>
                        <p className="text-xl text-gray-600 font-semibold">
                            Ne rate aucun événement !
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {upcomingEvents.map((event, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border-4 border-gray-200"
                            >
                                <div className={`absolute top-0 left-0 w-full h-2 rounded-t-3xl bg-gradient-to-r ${event.color}`}></div>

                                <div className="flex items-start gap-6">
                                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${event.color} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                        {event.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            {event.title}
                                        </h3>
                                        <div className="flex items-center gap-2 text-red-600 font-black mb-3">
                                            <Bell className="w-5 h-5" />
                                            {event.date}
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 font-semibold">
                                            <Gift className="w-5 h-5" />
                                            {event.reward}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Staff Team */}
            <section className="py-24 px-6 bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <h2 className="text-5xl font-black text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Notre Équipe
                        </h2>
                        <p className="text-xl text-gray-300 font-semibold">
                            Une équipe dévouée pour ton expérience
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {staffTeam.map((member, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -10 }}
                                className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700 text-center"
                            >
                                <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${member.color} flex items-center justify-center text-white mb-4 mx-auto group-hover:scale-110 transition-transform`}>
                                    {member.badge}
                                </div>
                                <div className={`text-sm font-black bg-gradient-to-r ${member.color} bg-clip-text text-transparent mb-2`}>
                                    {member.role}
                                </div>
                                <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {member.name}
                                </h3>
                                <p className="text-gray-400 font-semibold text-sm">
                                    {member.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-32 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        <div className="text-7xl mb-6">💬</div>
                        <h2 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Fais Partie de l'Aventure !
                        </h2>
                        <p className="text-2xl text-red-100 max-w-3xl mx-auto font-semibold mb-12">
                            Rejoins notre communauté et crée des souvenirs inoubliables
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <motion.button
                                onClick={() => window.open('https://discord.gg/3wS59h3Vbu', '_blank')}
                                className="inline-flex items-center gap-4 px-12 py-6 rounded-2xl bg-white text-red-600 font-black text-2xl shine-effect hover:bg-yellow-400 hover:text-red-700 transition-all shadow-2xl"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <MessageCircle className="w-8 h-8" />
                                REJOINDRE DISCORD
                            </motion.button>

                            <motion.button
                                className="inline-flex items-center gap-4 px-12 py-6 rounded-2xl bg-indigo-600 text-white font-black text-2xl shine-effect hover:bg-indigo-700 transition-all shadow-2xl"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Users className="w-8 h-8" />
                                VOIR LE FORUM
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative py-16 px-6 bg-gray-900 border-t-4 border-red-600">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-gray-400 font-semibold">
                        © 2025 PokeMovie. Serveur Cobblemon non-officiel. Pokémon © Nintendo/Game Freak.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Community;